import { useNavigate, useParams } from "react-router-dom";
import generalRequest from "../../request/generalRequest";
import { useEffect, useState, useContext, useRef } from "react";
import "./shopPage.css";
import { CombinedContext } from "../../context/combinedContext";
import BookingModal from "../bookingModal/bookingModal";
import translations from "../../translations/translations";
import { Flag, Phone } from "lucide-react";
import ReportReviewModalCustomer from "../reportReviewModal/reportReviewModalCustomer";
import ShopSkeleton from "./shopSkeleton/shopSkeleton";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";

function ShopCarousel({ images = [], rating = "5.0", reviews = 0 }) {
    const trackRef = useRef(null);
    const [idx, setIdx] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const startX = useRef(0);
    const currentX = useRef(0);
    const deltaX = useRef(0);

    const { language } = useContext(CombinedContext);

    const safeImages = images.length > 0 ? images : ["https://via.placeholder.com/900x500?text=No+Images"];

    const getImageUrl = (img) => {
        if (!img) return "https://via.placeholder.com/900x500?text=No+Image";
        return img.startsWith("http")
            ? img
            : `https://${process.env.REACT_APP_IMAGES_BUCKET}.s3.${process.env.REACT_APP_APP_REGION}.amazonaws.com/${img}`;
    };

    const goTo = (n) => {
        const total = safeImages.length;
        setIdx((n + total) % total);
    };

    const next = () => goTo(idx + 1);
    const prev = () => goTo(idx - 1);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [idx]);

    const onTouchStart = (e) => {
        setIsDragging(true);
        startX.current = e.touches ? e.touches[0].clientX : e.clientX;
        currentX.current = startX.current;
        deltaX.current = 0;
    };
    const onTouchMove = (e) => {
        if (!isDragging) return;
        currentX.current = e.touches ? e.touches[0].clientX : e.clientX;
        deltaX.current = currentX.current - startX.current;
        if (trackRef.current) {
            trackRef.current.style.transition = "none";
            trackRef.current.style.transform = `translateX(calc(${-idx * 100}% + ${deltaX.current}px))`;
        }
    };
    const onTouchEnd = () => {
        setIsDragging(false);
        if (Math.abs(deltaX.current) > 60) {
            if (deltaX.current < 0) next();
            else prev();
        }
        if (trackRef.current) {
            trackRef.current.style.transition = "";
            trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
        }
        deltaX.current = 0;
    };

    useEffect(() => {
        if (trackRef.current) {
            trackRef.current.style.transition = "transform 300ms ease";
            trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
        }
    }, [idx]);

    return (
        <div className="sf-shop-carousel" aria-roledescription="carousel">
            <div
                className="sf-shop-carousel-viewport"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseDown={(e) => {
                    e.preventDefault();
                    onTouchStart(e);
                    window.addEventListener("mousemove", onTouchMove);
                    window.addEventListener(
                        "mouseup",
                        () => {
                            onTouchEnd();
                            window.removeEventListener("mousemove", onTouchMove);
                        },
                        { once: true }
                    );
                }}
            >
                <div className="sf-shop-carousel-track" ref={trackRef}>
                    {safeImages.map((img, i) => (
                        <div className="sf-shop-carousel-slide" key={i} aria-hidden={i !== idx}>
                            <img
                                className="sf-shop-carousel-img"
                                src={getImageUrl(img)}
                                alt={`Gallery ${i + 1}`}
                                draggable={false}
                            />
                        </div>
                    ))}
                </div>

                {safeImages.length > 1 && (
                    <>
                        <button className="sf-shop-carousel-arrow sf-shop-carousel-arrow--left" onClick={prev}>
                            ‹
                        </button>
                        <button className="sf-shop-carousel-arrow sf-shop-carousel-arrow--right" onClick={next}>
                            ›
                        </button>
                    </>
                )}
            </div>

            {safeImages.length > 1 && (
                <div className="sf-shop-carousel-dots">
                    {safeImages.map((_, i) => (
                        <button
                            key={i}
                            className={`sf-shop-carousel-dot ${i === idx ? "is-active" : ""}`}
                            onClick={() => goTo(i)}
                            aria-label={`Go to slide ${i + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

const ShopPage = () => {
    const { shopId } = useParams();
    const { selectedService, setSelectedService, selectedstaff, setSelectedstaff, language } =
        useContext(CombinedContext);

    const [shopData, setShopData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isBookingOpen, setIsBookingOpen] = useState(false);
    const [showAllHours, setShowAllHours] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [reportReviewId, setReportReviewId] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);
    const categoryRefs = useRef({});

    const navigate = useNavigate();

    useEffect(() => {
        async function loadShop() {
            try {
                const res = await generalRequest.getShopWithstaffsByIdPublic(shopId);
                setShopData(res.shop);

                setLoading(false);
            } catch (err) {
                setError(err.message || String(err));
                setLoading(false);
            }
        }
        loadShop();
    }, [shopId]);

    const servicesByCategory = (shopData?.serviceCategories || [])
        .map((cat) => ({
            category: cat,
            services: (shopData.services || []).filter((s) => s.categoryId === cat.categoryId),
        }))
        .filter((group) => group.services.length > 0);

    useEffect(() => {
        if (!servicesByCategory.length) return;

        // If current activeCategory still exists, keep it
        const stillValid = servicesByCategory.some((group) => group.category.categoryId === activeCategory);

        if (stillValid) return;

        // Otherwise set first available
        setActiveCategory(servicesByCategory[0].category.categoryId);
    }, [servicesByCategory, activeCategory]);

    if (loading) return <ShopSkeleton />;
    if (error) return <p style={{ color: "red" }}>{error}</p>;

    const addressQuery = encodeURIComponent(`${shopData.address}, ${shopData.city}, ${shopData.country}`);

    const openBookingForService = (serviceId) => {
        setSelectedService(serviceId);
        setIsBookingOpen(true);
    };

    function formatPhone(number) {
        if (!number) return "";
        // remove all non-digits
        const digits = number.replace(/\D/g, "");

        // US/Canada format (E.164: +1xxxxxxxxxx)
        if (digits.length === 11 && digits.startsWith("1")) {
            return "+1 " + "(" + digits.slice(1, 4) + ") " + digits.slice(4, 7) + "-" + digits.slice(7);
        }

        // fallback (just add + if missing)
        return number.startsWith("+") ? number : "+" + number;
    }

    function handleReportReview(reviewId) {
        setReportReviewId(reviewId);
        setReportOpen(true);
    }

    const rating = shopData.reviews?.averageRating?.toFixed(1) || "5.0";
    const reviewCount = shopData.reviews?.total || 0;

    function formatTime24To12(time24, language = "en") {
        if (!time24) return "";

        const [h, m] = time24.split(":").map(Number);

        const period = h >= 12 ? "PM" : "AM";
        const hour12 = h % 12 || 12;

        // Spanish usually prefers 24h, but if you want AM/PM anyway:
        if (language === "es") {
            return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
        }

        return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
    }

    const formatUrl = (url) => {
        if (!url) return "";
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        return `https://${url}`;
    };

    return (
        <div className="sf-shop-container">
            <div className="sf-shop-content">
                <div className="sf-shop-main">
                    <ShopCarousel images={shopData.images || []} rating={rating} reviews={reviewCount} />

                    <h1 className="sf-shop-name">{shopData.name}</h1>
                    <p className="sf-shop-location">{shopData.address}</p>
                    <p className="sf-shop-description">{shopData.description}</p>

                    <h2 className="sf-shop-section-title">{translations[language].shopPage.main.services}</h2>
                    {shopData.services?.length > 0 ? (
                        <div className="sf-shop-service-list">
                            {/* 🔝 STICKY CATEGORY TABS */}
                            <div className="sf-profile-tabs sf-profile-tabs--sticky">
                                {servicesByCategory.map(({ category }) => (
                                    <button
                                        key={category.categoryId}
                                        className={`sf-profile-tab-btn ${
                                            activeCategory === category.categoryId ? "active" : ""
                                        }`}
                                        onClick={() => {
                                            setActiveCategory(category.categoryId);
                                            categoryRefs.current[category.categoryId]?.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            });
                                        }}
                                    >
                                        {category.name?.[language]}
                                    </button>
                                ))}
                            </div>

                            {/* 📦 SERVICES BY CATEGORY */}
                            {servicesByCategory.map(({ category, services }) => (
                                <div
                                    key={category.categoryId}
                                    ref={(el) => (categoryRefs.current[category.categoryId] = el)}
                                    className="sf-shop-category-section"
                                >
                                    <h3 className="sf-shop-category-title">{category.name?.[language]}</h3>

                                    {services.map((s) => (
                                        <div key={s.serviceId}>
                                            <div className="sf-shop-service-item">
                                                <div className="sf-shop-service-info">
                                                    <div className="sf-shop-service-name">
                                                        {s.name}
                                                        {s.isHomeService && (
                                                            <span className="sf-shop-service-home">
                                                                <span className="sf-shop-dot"> · </span>
                                                                {language === "es" ? "A domicilio" : "Home service"}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="sf-shop-service-duration">{s.description}</div>
                                                </div>

                                                <div className="sf-shop-service-right">
                                                    <div>
                                                        <div className="sf-shop-service-price">
                                                            {s.currency || "USD"} {s.price ?? 0}
                                                        </div>
                                                        <div className="sf-shop-service-duration">{s.duration} min</div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="sf-shop-service-book-btn"
                                                        onClick={() => openBookingForService(s.serviceId)}
                                                    >
                                                        {translations[language].shopPage.main.book}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="sf-service-divider-shop" />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="sf-shop-empty">{translations[language].shopPage.main.noServices}</p>
                    )}

                    <div className="sf-shop-attr-section">
                        <h2 className="sf-shop-attr-title">Attributes</h2>

                        {shopData.attributes?.length > 0 ? (
                            <div className="sf-shop-attr-grid">
                                {shopData.attributes.map((attr) => (
                                    <div className="sf-shop-attr-chip" key={attr.attributeId}>
                                        {attr.icon ? (
                                            <img src={attr.icon} className="sf-shop-attr-chip-icon" alt={attr.nameEN} />
                                        ) : (
                                            <div className="sf-shop-attr-chip-placeholder" />
                                        )}

                                        <span className="sf-shop-attr-chip-label">
                                            {language === "es" ? attr.nameES : attr.nameEN}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="sf-shop-attr-empty">No attributes listed</div>
                        )}
                    </div>

                    <div className="sf-shop-review-section">
                        {shopData.reviews?.length > 0 && (
                            <div className="sf-shop-review-summary">
                                {/* Left: Average Score */}
                                <div className="sf-shop-review-summary-left">
                                    <div className="sf-shop-review-summary-score">
                                        {(
                                            shopData.reviewSummary?.averageRating ||
                                            shopData.reviews.reduce((sum, r) => sum + r.rating, 0) /
                                                shopData.reviews.length
                                        ).toFixed(1)}
                                    </div>
                                    <div className="sf-shop-review-summary-total">
                                        {shopData.reviews.length} {translations[language].shopPage.main.reviews}
                                    </div>
                                </div>

                                {/* Right: Rating Bars */}
                                <div className="sf-shop-review-summary-bars">
                                    {[5, 4, 3, 2, 1].map((star) => {
                                        const count = shopData.reviews.filter((r) => r.rating === star).length;
                                        const total = shopData.reviews.length;
                                        const percent = total > 0 ? (count / total) * 100 : 0;

                                        return (
                                            <div key={star} className="sf-shop-review-summary-row">
                                                <div className="sf-shop-review-summary-star">
                                                    <span className="sf-shop-review-summary-star-count">{star}</span> ★
                                                </div>
                                                <div className="sf-shop-review-summary-bar">
                                                    <div
                                                        className="sf-shop-review-summary-bar-fill"
                                                        style={{ width: `${percent}%` }}
                                                    />
                                                </div>
                                                <div className="sf-shop-review-summary-count">{count}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <h2 className="sf-shop-review-title">
                            {translations[language].shopPage.main.reviews || "Reviews"}
                        </h2>

                        {Array.isArray(shopData.reviews) && shopData.reviews.length > 0 ? (
                            <div className="sf-shop-review-list">
                                {shopData.reviews.map((rev) => (
                                    <div key={rev.reviewId} className="sf-shop-review-item">
                                        {/* Header Row */}
                                        <div className="sf-shop-review-header">
                                            <div className="sf-shop-review-header-left">
                                                <div className="sf-shop-review-name">
                                                    {rev.customerName || "Anonymous"}
                                                </div>
                                            </div>
                                            <div className="sf-review-header-right">
                                                <div className="sf-review-rating">{rev.rating} ★</div>

                                                {/* REPORT BUTTON */}
                                                <button
                                                    className="sf-review-report-btn"
                                                    onClick={() => handleReportReview(rev.reviewId)}
                                                    title="Report review"
                                                >
                                                    <Flag size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Services */}
                                        {rev.services?.length > 0 && (
                                            <div className="sf-shop-review-services">
                                                {rev.services.map((srv, i) => (
                                                    <div key={i} className="sf-shop-review-service-chip">
                                                        {language === "es" ? srv.serviceNameEs : srv.serviceNameEn}
                                                        &nbsp;•&nbsp;
                                                        <span className="sf-shop-review-service-duration">
                                                            {srv.duration} min
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Comment */}
                                        <div className="sf-shop-review-comment">
                                            {rev.comment || "No comment provided."}
                                        </div>

                                        {/* Date */}
                                        <div className="sf-shop-review-date">
                                            {new Date(rev.createdAt).toLocaleDateString()}
                                        </div>

                                        <div className="sf-shop-review-divider" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="sf-shop-review-empty">
                                {translations[language].shopPage.main.noReviews || "No reviews yet"}
                            </div>
                        )}
                    </div>
                </div>

                <div className="sf-shop-sidebar">
                    <div className="sf-shop-sidebar-section">
                        <h3 className="sf-shop-sidebar-title">{translations[language].shopPage.sidebar.location}</h3>
                        <div className="sf-shop-map-wrap">
                            <ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
                        </div>

                        <a
                            className="sf-shop-directions-btn"
                            href={`https://www.google.com/maps/dir/?api=1&destination=${addressQuery}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {translations[language].shopPage.sidebar.getDirections}
                        </a>
                    </div>
                    <div className="sf-shop-sidebar-section">
                        <h3 className="sf-shop-sidebar-title">{translations[language].shopPage.sidebar.staff}</h3>
                        <div className="sf-shop-staff-list">
                            {shopData.staffs?.map((b) => (
                                <div
                                    className="sf-shop-staff-item"
                                    key={b.staffId}
                                    onClick={() => navigate(`/shops/${shopData.shopId}/staff/${b.staffId}`)}
                                >
                                    {b.photo ? (
                                        <img
                                            className="sf-shop-staff-avatar"
                                            src={b.photo}
                                            alt={b.firstName || "Staff"}
                                        />
                                    ) : (
                                        <div className="sf-shop-staff-avatar-fallback">
                                            <span>{b.firstName?.charAt(0).toUpperCase()}</span>
                                        </div>
                                    )}

                                    <div className="sf-shop-staff-name">{b.firstName}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="sf-shop-sidebar-section">
                        <h3 className="sf-shop-sidebar-title">
                            {translations[language].shopPage.sidebar.contactAndHours}
                        </h3>

                        {/* CONTACT ROW */}
                        <div className="sf-shop-contact-row">
                            {/* PHONE + TEXT (entire thing clickable) */}
                            <a
                                href={shopData.phone ? `tel:${shopData.phone}` : undefined}
                                className="sf-shop-contact-info"
                            >
                                <Phone size={16} strokeWidth={1.6} className="sf-shop-contact-icon" />
                                <span>
                                    {shopData.phone
                                        ? formatPhone(shopData.phone)
                                        : translations[language].profile.sidebar.contact.noPhone}
                                </span>
                            </a>

                            {/* CALL BUTTON */}
                            {shopData.phone && (
                                <a href={`tel:${shopData.phone}`} className="sf-shop-call-btn">
                                    {translations[language].shopPage.sidebar.call}
                                </a>
                            )}
                        </div>
                        <hr className="sf-shop-divider" />

                        {/* HOURS */}
                        {Array.isArray(shopData.openingHours) && shopData.openingHours.length > 0 && (
                            <div className="sf-shop-hours">
                                <div className="sf-shop-hours-full">
                                    {shopData.openingHours.map((dayObj, index) => {
                                        const todayIndex = (new Date().getDay() + 6) % 7;
                                        const isToday = todayIndex === index;
                                        const dayLabel = language === "es" ? dayObj.dayES : dayObj.dayEN;
                                        const isClosed = dayObj.closed;

                                        return (
                                            <div
                                                key={index}
                                                className={`sf-shop-hours-row ${isToday ? "is-today" : ""}`}
                                            >
                                                <div className="sf-shop-hours-day">{dayLabel}</div>
                                                <div className="sf-shop-hours-time">
                                                    {isClosed
                                                        ? translations[language].shopPage.sidebar.hours.closed
                                                        : `${formatTime24To12(
                                                              dayObj.open,
                                                              language
                                                          )} - ${formatTime24To12(dayObj.close, language)}`}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* SOCIAL MEDIA */}

                        {shopData.socialLinks && Object.values(shopData.socialLinks).some((link) => link) && (
                            <div>
                                <hr className="sf-shop-divider" />
                                <h4 className="sf-shop-social-title">
                                    {translations[language].shopPage.sidebar.socialTitle}
                                </h4>
                                <div className="sf-shop-socials">
                                    {/* Instagram */}

                                    {shopData.socialLinks.instagram && (
                                        <div className="sf-social-item">
                                            <a
                                                href={formatUrl(shopData.socialLinks.instagram)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="sf-social-link"
                                            >
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/1384/1384031.png"
                                                    alt="Instagram"
                                                />
                                            </a>
                                            <p>Instagram</p>
                                        </div>
                                    )}

                                    {/* Facebook */}
                                    {shopData.socialLinks.facebook && (
                                        <div className="sf-social-item">
                                            <a
                                                href={formatUrl(shopData.socialLinks.facebook)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="sf-social-link"
                                            >
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/1384/1384005.png"
                                                    alt="Facebook"
                                                />
                                            </a>
                                            <p>Facebook</p>
                                        </div>
                                    )}

                                    {/* TikTok */}
                                    {shopData.socialLinks.tiktok && (
                                        <div className="sf-social-item">
                                            <a
                                                href={formatUrl(shopData.socialLinks.tiktok)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="sf-social-link"
                                            >
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/3046/3046120.png"
                                                    alt="TikTok"
                                                />
                                            </a>
                                            <p>TikTok</p>
                                        </div>
                                    )}

                                    {/* Website */}
                                    {shopData.socialLinks.website && (
                                        <div className="sf-social-item">
                                            <a
                                                href={formatUrl(shopData.socialLinks.website)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="sf-social-link"
                                            >
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/545/545674.png"
                                                    alt="Website"
                                                />
                                            </a>
                                            <p>Website</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isBookingOpen && (
                <BookingModal
                    open={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    availabilityId={shopData.shopId}
                    shopData={shopData}
                    initialServiceId={selectedService}
                    initialstaffId={selectedstaff}
                />
            )}

            {reportOpen && (
                <ReportReviewModalCustomer
                    open={reportOpen}
                    onClose={() => setReportOpen(false)}
                    reviewId={reportReviewId}
                    shopId={shopId}
                />
            )}
        </div>
    );
};

export default ShopPage;
